import type { GridAdapter } from "./adapters/GridAdapter";
import type { GridLayout } from "./models/GridInstance";
import {
  DEFAULT_GRID_LAYOUT_PRESETS,
  createGridInstanceId,
  normalizeGridLayoutMinWidth,
} from "./models/GridInstance";

export interface GridExportInstance {
  id: string;
  type: string;
  visible: boolean;
  name?: string;
  settings: Record<string, any>;
}

export interface GridExportLayout {
  id: string;
  name: string;
  minWidth: number;
  grids: GridExportInstance[];
}

export interface GridExportData {
  v: 2;
  source: "StringDevLayout";
  selectedLayout: string;
  layouts: GridExportLayout[];
}

export class GridSerializer {
  static readonly VERSION = 2 as const;

  static serialize(layouts: GridLayout[], selectedLayoutId: string): GridExportData {
    return {
      v: this.VERSION,
      source: "StringDevLayout",
      selectedLayout: selectedLayoutId,
      layouts: layouts.map((layout) => ({
        id: layout.id,
        name: layout.name,
        minWidth: layout.minWidth,
        grids: layout.instances.map((instance) => ({
          id: instance.id,
          type: instance.type,
          visible: instance.visible,
          ...(instance.name !== undefined ? { name: instance.name } : {}),
          settings: { ...instance.settings },
        })),
      })),
    };
  }

  static deserialize(
    data: unknown,
    adapterRegistry: Map<string, GridAdapter>,
  ): { layouts: GridLayout[]; selectedLayoutId: string } | null {
    if (!data || typeof data !== "object") {
      return null;
    }

    const object = data as Record<string, unknown>;
    const version = object["v"];
    const source = object["source"];
    if ((source !== "StringDevLayout" && source !== "StringGrid") || (version !== 1 && version !== 2)) {
      return null;
    }
    if (!Array.isArray(object["layouts"])) {
      return null;
    }

    const layouts: GridLayout[] = (object["layouts"] as unknown[])
      .filter(
        (layout): layout is Record<string, unknown> =>
          !!layout &&
          typeof layout === "object" &&
          typeof layout["id"] === "string" &&
          typeof layout["name"] === "string",
      )
      .map((layout, index) => ({
        id: layout["id"] as string,
        name: layout["name"] as string,
        minWidth: this.resolveImportedMinWidth(layout["minWidth"], index),
        instances: (Array.isArray(layout["grids"]) ? layout["grids"] : [])
          .filter(
            (grid): grid is Record<string, unknown> =>
              !!grid &&
              typeof grid === "object" &&
              typeof grid["type"] === "string" &&
              adapterRegistry.has(grid["type"] as string),
          )
          .map((grid) => {
            const type = grid["type"] as string;
            const adapter = adapterRegistry.get(type)!;
            return {
              id: typeof grid["id"] === "string" ? grid["id"] : createGridInstanceId(),
              type,
              visible: grid["visible"] !== false,
              ...(typeof grid["name"] === "string" && grid["name"]
                ? { name: grid["name"] }
                : {}),
              settings: {
                ...adapter.getDefaults(),
                ...(grid["settings"] &&
                typeof grid["settings"] === "object" &&
                !Array.isArray(grid["settings"])
                  ? (grid["settings"] as Record<string, unknown>)
                  : {}),
              },
            };
          }),
      }));

    if (layouts.length === 0) {
      return null;
    }

    layouts.sort((left, right) => right.minWidth - left.minWidth);

    const rawSelected =
      typeof object["selectedLayout"] === "string"
        ? object["selectedLayout"]
        : typeof object["activeLayout"] === "string"
          ? object["activeLayout"]
          : layouts[0].id;

    const selectedLayoutId = layouts.some((layout) => layout.id === rawSelected)
      ? rawSelected
      : layouts[0].id;

    return { layouts, selectedLayoutId };
  }

  static toFile(filename: string, data: GridExportData): void {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  static fromFile(): Promise<unknown> {
    return new Promise<unknown>((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".json,application/json";

      const cleanup = () => input.remove();

      input.addEventListener("change", () => {
        const file = input.files?.[0];
        if (!file) {
          cleanup();
          reject(new Error("No file selected"));
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          cleanup();
          try {
            resolve(JSON.parse(reader.result as string));
          } catch {
            reject(new Error("Invalid JSON"));
          }
        };
        reader.onerror = () => {
          cleanup();
          reject(new Error("File read error"));
        };
        reader.readAsText(file);
      });

      input.addEventListener("cancel", () => {
        cleanup();
        reject(new Error("Cancelled"));
      });

      document.body.appendChild(input);
      input.click();
    });
  }

  private static resolveImportedMinWidth(value: unknown, index: number): number {
    if (typeof value === "number" && Number.isFinite(value)) {
      return normalizeGridLayoutMinWidth(value);
    }

    const preset = DEFAULT_GRID_LAYOUT_PRESETS[index];
    if (preset) {
      return preset.minWidth;
    }

    const lastPreset = DEFAULT_GRID_LAYOUT_PRESETS[DEFAULT_GRID_LAYOUT_PRESETS.length - 1];
    return Math.max(0, lastPreset.minWidth - (index - (DEFAULT_GRID_LAYOUT_PRESETS.length - 1)) * 160);
  }
}
